import { yupResolver } from '@hookform/resolvers/yup';
import { BenefitDto } from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { YesNo } from '@zorro/types';
import {
  useFlowId,
  useForm,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { AgentOrEmployee } from './AgentOrEmployeeInput';
import {
  InitialPremiumProgress,
  MONTHLY_AUTO_PAY_END_OF_MONTH_VALUE,
  PaymentStatusFormInput,
  PaymentStatusFormOutput,
  getHandlerAndCompletedFromAutoPayStatus,
  getHandlerAndProgressFromInitialPremiumPaymentStatus,
  paymentStatusSchema,
  toUpdateBenefitPaymentStatusDto,
} from './PaymentStatusUtils';

export const usePaymentStatusForm = (
  benefitId: BenefitDto['id'] | undefined,
  isFinalizationMode: boolean = true,
  shouldShowLoadingOverlay: boolean = true
) => {
  const flowId = useFlowId();

  const { isLoading, data: benefit } = useMonolithQuery({
    method: 'benefitsControllerGetBenefit',
    params: benefitId && [benefitId],
  });

  const { tryMutate: updatePaymentStatus } = useMonolithMutation({
    method: 'benefitsControllerUpdatePaymentStatus',
    shouldReloadPage: false,
    shouldShowLoadingOverlay,
  });

  const initialPremiumDefaults =
    getHandlerAndProgressFromInitialPremiumPaymentStatus(
      benefit?.initialPremiumPaymentStatus
    );
  const autoPayDefaults = getHandlerAndCompletedFromAutoPayStatus(
    benefit?.autoPayStatus
  );

  const paymentStatusForm = useForm<PaymentStatusFormInput>({
    mode: 'all',
    resolver: yupResolver(paymentStatusSchema),
    defaultValues: {
      initialPremiumHandler: isFinalizationMode
        ? (initialPremiumDefaults.handler ?? AgentOrEmployee.AGENT)
        : initialPremiumDefaults.handler,
      initialPremiumCompleted: isFinalizationMode
        ? (initialPremiumDefaults.progress ?? InitialPremiumProgress.NOT_SET_UP)
        : initialPremiumDefaults.progress,
      autoPaySetter: isFinalizationMode
        ? (autoPayDefaults.handler ?? AgentOrEmployee.AGENT)
        : autoPayDefaults.handler,
      autoPayCompleted: isFinalizationMode
        ? (autoPayDefaults.completed ?? YesNo.NO)
        : autoPayDefaults.completed,
      monthlyAutoPayDate: benefit?.isMonthlyAutoPayDateEndOfMonth
        ? MONTHLY_AUTO_PAY_END_OF_MONTH_VALUE
        : (benefit?.monthlyAutoPayDate?.toString() ?? undefined),
    },
  });

  const isValid = paymentStatusForm.formState.isValid && isDefined(benefit);

  async function onSubmit(data: PaymentStatusFormOutput): Promise<boolean> {
    if (!benefitId) {
      return false;
    }

    const result = await updatePaymentStatus(
      [benefitId, toUpdateBenefitPaymentStatusDto(data)],
      { flowId }
    );

    return result.isOk();
  }

  return {
    isLoading,
    isValid,
    benefit,
    paymentStatusForm,
    onSubmit,
    handleSubmit: paymentStatusForm.handleSubmit,
  };
};
